import React, { useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import { EntityRoutes } from 'routes/constants';
import strings from 'strings';
import FPChip from 'UI/components/atoms/FPChip';
import InfoLabel, { InfoLabelModes } from 'UI/components/molecules/InfoLabel';

import { ExpandableList } from './ExpandableList';
import {
  ASSOCIATIONS_DISPLAY_LIMIT,
  CERTIFICATIONS_DISPLAY_THRESHOLD,
  EXPERIENCE_DISPLAY_THRESHOLD,
  SKILLS_DISPLAY_THRESHOLD
} from './ResumeCard.constants';
import { useResumeCardStyles } from './ResumeCard.styled';
import {
  extractResumeData,
  formatDescription,
  formatEducationPeriod,
  getExpandButtonText,
  getPeriodString
} from './ResumeCard.utils';
import { ResumeCardActions } from './ResumeCardActions';
import { ResumeSection } from './ResumeSection';

export const ResumeCard = React.memo(
  ({ resume, candidateTypeColor, candidateStatusColor, isSelected = false, onSelectionChange }) => {
    const classes = useResumeCardStyles();
    const [isSkillsExpanded, setIsSkillsExpanded] = React.useState(false);

    const {
      fullName,
      profession,
      location,
      experiences,
      educations,
      certifications,
      associations,
      skills,
      phoneNumbers,
      emails,
      candidateType,
      candidateStatus,
      createdAt,
      lastActivity,
      functionalTitle,
      industry,
      specialty,
      subSpecialty
    } = extractResumeData(resume);

    const metadata = {
      typeLabel: candidateType?.title,
      statusLabel: candidateStatus?.title,
      createdAt,
      lastActivity
    };
    const hasTypeChip = Boolean(metadata.typeLabel);
    const hasStatusChip = Boolean(metadata.statusLabel);
    const candidateId = resume?.candidate?.id;
    const shouldRenderChipRow = hasTypeChip || hasStatusChip;
    const shouldRenderSpecialtyInfo = Boolean(specialty || subSpecialty || industry);

    const renderExperienceItem = useCallback(
      (exp, index) => (
        <Box key={`${exp.company}-${exp.title}-${index}`} mb={0.5}>
          {exp.title && <Typography component="span"> {exp.title} •&nbsp;</Typography>}
          <Typography variant="body2" component="span">
            {exp.company}
            {exp.location && ` • ${exp.location}`}
          </Typography>
          {(exp.start_date || exp.end_date) && (
            <Typography variant="body2" component="span">
              &nbsp;({getPeriodString(exp.start_date, exp.end_date, exp.is_current)})
            </Typography>
          )}
        </Box>
      ),
      []
    );

    const renderEducationItem = useCallback(
      (edu, index) => (
        <Box key={`${edu.issuing_organization}-${edu.end_year}-${index}`} mb={0.5}>
          <Typography component="span">{edu.issuing_organization} •&nbsp;</Typography>
          {edu.degree && (
            <Typography variant="body2" component="span">
              {edu.degree}
            </Typography>
          )}
          {(edu.start_year || edu.end_year) && (
            <Typography variant="body2" component="span">
              &nbsp;({formatEducationPeriod(edu.start_year, edu.end_year)})
            </Typography>
          )}
          {edu.description && (
            <Typography variant="body2" display="block" className={classes.descriptionText}>
              {formatDescription(edu.description)}
            </Typography>
          )}
        </Box>
      ),
      [classes.descriptionText]
    );

    const renderCertificationItem = useCallback(
      (cert, index) => (
        <Box key={`${cert.issuing_organization}-${index}`} mb={0.5}>
          {cert.description && (
            <Typography component="span" className={classes.descriptionText}>
              {formatDescription(cert.description)}
            </Typography>
          )}
          {cert.issuing_organization && (
            <Typography variant="body2" component="span">
              {cert.description && <>&nbsp;• </>}
              {cert.issuing_organization}
            </Typography>
          )}
          {cert.issued_date?.year && (
            <Typography variant="body2" component="span">
              &nbsp;({cert.issued_date.year})
            </Typography>
          )}
        </Box>
      ),
      [classes.descriptionText]
    );

    const renderAssociationItem = useCallback(
      (assoc, index) => (
        <Box key={`${assoc.issuing_organization}-${assoc.role}-${index}`} mb={0.5}>
          <Typography component="span">{assoc.issuing_organization} •&nbsp;</Typography>
          {assoc.role && (
            <Typography variant="body2" component="span">
              {assoc.role}
            </Typography>
          )}
          {(assoc.start_date || assoc.end_date) && (
            <Typography variant="body2" component="span">
              &nbsp;({getPeriodString(assoc.start_date, assoc.end_date)})
            </Typography>
          )}
        </Box>
      ),
      []
    );

    const expandExperienceText = useMemo(
      () => getExpandButtonText({ items: experiences }),
      [experiences]
    );
    const expandSkillsText = useMemo(() => getExpandButtonText({ items: skills }), [skills]);
    const expandCertificationsText = useMemo(
      () => getExpandButtonText({ items: certifications }),
      [certifications]
    );
    const associationOverflowText = useMemo(
      () => getExpandButtonText({ items: associations }),
      [associations]
    );

    const displayedSkills = useMemo(
      () => (isSkillsExpanded ? skills : skills.slice(0, SKILLS_DISPLAY_THRESHOLD)),
      [isSkillsExpanded, skills]
    );

    const handleCheckboxChange = useCallback(() => {
      if (onSelectionChange) {
        onSelectionChange(resume.candidate?.id);
      }
    }, [onSelectionChange, resume]);

    return (
      <li className={classes.resumeItem}>
        <Box className={classes.actionsContainer}>
          <ResumeCardActions
            candidateId={resume.candidate?.id}
            phone={phoneNumbers[0] || ''}
            email={emails[0] || ''}
          />
        </Box>
        <Box className={classes.checkboxContainer}>
          <Checkbox
            checked={isSelected}
            color="primary"
            data-testid={`resume-card-checkbox-${resume.candidate?.id}`}
            inputProps={{ 'aria-label': `Select ${fullName}` }}
            onChange={handleCheckboxChange}
          />
        </Box>
        <Box className={classes.resumeContent}>
          <Box className={classes.resumeHeader}>
            <Box className={classes.resumeInfoBox}>
              <Typography variant="h6" display="inline">
                {candidateId ? (
                  <Link
                    component={RouterLink}
                    to={EntityRoutes.CandidateProfile.replace(':id', candidateId)}
                  >
                    {fullName}
                  </Link>
                ) : (
                  fullName
                )}
                {location && (
                  <Typography component="span" color="textSecondary">
                    &nbsp;• {location}
                  </Typography>
                )}
              </Typography>
              {shouldRenderChipRow && (
                <div className={classes.metadataRow}>
                  {hasTypeChip && (
                    <FPChip
                      data-testid="resume-card-type-chip"
                      label={candidateType.title}
                      color={candidateTypeColor}
                      size="small"
                    />
                  )}
                  {hasStatusChip && (
                    <FPChip
                      data-testid="resume-card-status-chip"
                      label={candidateStatus.title}
                      color={candidateStatusColor}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </div>
              )}
            </Box>
            {profession && (
              <Typography variant="body1">
                {profession} {functionalTitle && `• ${functionalTitle}`}
              </Typography>
            )}
            {shouldRenderSpecialtyInfo && (
              <Typography variant="body2">
                {[industry, specialty, subSpecialty].filter(Boolean).join(' › ')}
              </Typography>
            )}
          </Box>

          <ResumeSection
            isEmpty={experiences.length === 0}
            title={strings.resumeSearch.resumeCard.sections.experience}
          >
            <ExpandableList
              items={experiences}
              displayThreshold={EXPERIENCE_DISPLAY_THRESHOLD}
              renderItem={renderExperienceItem}
              expandButtonText={expandExperienceText}
              collapseButtonText={strings.resumeSearch.resumeCard.showFewer}
            />
          </ResumeSection>

          <ResumeSection
            isEmpty={skills.length === 0}
            title={strings.resumeSearch.resumeCard.sections.skills}
          >
            <>
              <InfoLabel description={displayedSkills} mode={InfoLabelModes.CHIP} />
              {skills.length > SKILLS_DISPLAY_THRESHOLD && (
                <Button
                  variant="text"
                  size="small"
                  color="secondary"
                  disableRipple
                  onClick={() => setIsSkillsExpanded(!isSkillsExpanded)}
                >
                  {isSkillsExpanded ? strings.resumeSearch.resumeCard.showFewer : expandSkillsText}
                </Button>
              )}
            </>
          </ResumeSection>

          <ResumeSection
            isEmpty={educations.length === 0}
            title={strings.resumeSearch.resumeCard.sections.education}
          >
            {educations.map(renderEducationItem)}
          </ResumeSection>

          <ResumeSection
            isEmpty={certifications.length === 0}
            title={strings.resumeSearch.resumeCard.sections.certifications}
          >
            <ExpandableList
              items={certifications}
              displayThreshold={CERTIFICATIONS_DISPLAY_THRESHOLD}
              renderItem={renderCertificationItem}
              expandButtonText={expandCertificationsText}
              collapseButtonText={strings.resumeSearch.resumeCard.showFewer}
            />
          </ResumeSection>

          <ResumeSection
            isEmpty={associations.length === 0}
            title={strings.resumeSearch.resumeCard.sections.associations}
          >
            {associations.slice(0, ASSOCIATIONS_DISPLAY_LIMIT).map(renderAssociationItem)}
            {associations.length > ASSOCIATIONS_DISPLAY_LIMIT && (
              <Typography variant="caption">{associationOverflowText}</Typography>
            )}
          </ResumeSection>
        </Box>
      </li>
    );
  }
);
